/**
 * 261002 batch 2 (A2) — "Xem bản gửi engine": logic THUẦN (tách khỏi file import antd để jest chạy được).
 */
import type { EnginePromptPayload } from '../../types/film';

/** `roleLine` (261010 P4-1): câu khai vai CODE dựng cho ảnh storyboard / sơ đồ — chỉ ảnh sky mới có. */
export type EngineRef = EnginePromptPayload['refs'][number];

const KIND_VI: Record<string, string> = {
  crowd:'Đám đông nền (nhóm)',look: 'Bộ đồ (mặt + đồ)', char: 'Mặt nhân vật', loc: 'Bối cảnh', prop: 'Đạo cụ', voice: 'Giọng', outfit: 'Ảnh tham chiếu',
  skyStoryboard: 'Storyboard (bố cục)', skyPositionMap: 'Sơ đồ định vị (chỗ đứng)',
};
export const refKindLabel = (kind: string): string => KIND_VI[kind] || kind;

/** Dòng mô tả một ảnh: `@Image3 · Đạo cụ · Assassin's sword`. */
export function refCaption(r: EngineRef): string {
  return `${r.slot} · ${refKindLabel(r.kind)} · ${r.label}`;
}

/** Tách ảnh và audio để hiển thị riêng; ảnh theo thứ tự @ImageN. */
export function splitEngineRefs(refs: EngineRef[]): { images: EngineRef[]; audios: EngineRef[] } {
  const n = (r: EngineRef) => Number(r.slot.replace(/\D/g, '')) || 0;
  return {
    images: refs.filter((r) => r.slot.startsWith('@Image')).sort((a, b) => n(a) - n(b)),
    audios: refs.filter((r) => r.slot.startsWith('@Audio')).sort((a, b) => n(a) - n(b)),
  };
}

/** Prompt có dấu chip ⇒ ảnh đạo cụ gửi đi = đúng các chip trong prompt (hiện ở đầu modal). */
export const chipNote = (chipAuthoritative: boolean): string =>
  chipAuthoritative
    ? 'Ảnh đạo cụ gửi đi = đúng các chip "Props in this scene" trong prompt (xoá chip ⇒ ảnh bị gỡ).'
    : 'Prompt cũ (chưa có dòng "Props in this scene"): ảnh đạo cụ do hệ thống tự thêm lúc gen. Bấm "Dựng lại prompt" để chip hiện đủ.';

const ENGINE_VI: Record<string, string> = { SEEDANCE_25: 'Seedance 2.5', SEEDANCE_20: 'Seedance 2.0' };
export const engineLabel = (engine?: string | null): string | null => (engine ? (ENGINE_VI[engine] || engine) : null);

/**
 * Dòng đầu modal: engine · số giây · model của CHÍNH lần gen này (261010 P4-1). Mỗi phần có mới hiện; plan không sky không có `engine`;
 * BE cũ không có trường nào ⇒ `null` (modal không vẽ dòng này, hiện như trước).
 */
export function engineHeadline(d: { engine?: string | null; durationS?: number | null; model?: string | null }): string | null {
  const parts = [engineLabel(d.engine), typeof d.durationS === 'number' ? `${d.durationS} giây` : null, d.model ? `model ${d.model}` : null].filter(Boolean) as string[];
  return parts.length ? parts.join(' · ') : null;
}

/** Ảnh/đạo cụ KHÔNG đính được + lý do — bỏ rỗng/trùng; BE cũ không có ⇒ []. */
export const droppedLines = (dropped?: Array<string | null | undefined> | null): string[] =>
  Array.from(new Set((dropped || []).filter((x): x is string => typeof x === 'string' && x.trim() !== '')));

/** Gom MỌI thứ modal cần từ payload (mới hoặc cũ) — để logic hiển thị test được mà không cần antd. */
export function viewOf(d: Pick<EnginePromptPayload, 'refs'> & Partial<EnginePromptPayload>): { headline: string | null; images: EngineRef[]; audios: EngineRef[]; dropped: string[] } {
  const { images, audios } = splitEngineRefs(d.refs || []);
  return { headline: engineHeadline(d), images, audios, dropped: droppedLines(d.dropped) };
}
